import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { 
  ArrowLeft, Building2, Users, Search, Eye, Filter, 
  CheckCircle2, AlertCircle, UserX, User, Building, Download
} from 'lucide-react';
import { toast } from 'sonner';

export default function CompanyEmployees() {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [company, setCompany] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');

  useEffect(() => {
    if (user?.role !== 'super_admin') {
      toast.error('Accès non autorisé');
      navigate('/dashboard');
      return;
    }
    fetchCompany();
    fetchEmployees();
    fetchDepartments();
  }, [companyId, user]);

  const fetchCompany = async () => {
    try {
      const response = await api.get(`/companies/${companyId}`);
      setCompany(response.data);
    } catch (error) {
      toast.error('Entreprise non trouvée');
      navigate('/companies');
    }
  };

  const fetchEmployees = async () => {
    try {
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (statusFilter) params.status = statusFilter;
      if (departmentFilter) params.department_id = departmentFilter;
      
      const response = await api.get(`/companies/${companyId}/employees`, { params });
      setEmployees(response.data.employees || []);
    } catch (error) {
      toast.error('Erreur lors du chargement des employés');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      // For super admin, we need to get departments for this specific company
      const response = await api.get('/departments');
      // Filter departments by company_id
      const companyDepts = response.data.filter(d => d.company_id === companyId);
      setDepartments(companyDepts);
    } catch (error) {
      console.error('Erreur chargement directions:', error);
    }
  };

  const handleSearch = () => {
    setLoading(true);
    fetchEmployees();
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { className: 'bg-green-100 text-green-800', label: 'Actif', icon: CheckCircle2 },
      suspended: { className: 'bg-amber-100 text-amber-800', label: 'Suspendu', icon: AlertCircle },
      terminated: { className: 'bg-red-100 text-red-800', label: 'Renvoyé', icon: UserX }
    };
    const config = styles[status] || styles.active;
    const Icon = config.icon;
    return (
      <Badge className={config.className}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getLogoUrl = (logoPath) => {
    if (!logoPath) return null;
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    return `${backendUrl}${logoPath}`;
  };

  // Stats
  const stats = {
    total: employees.length,
    active: employees.filter(e => e.status === 'active' || !e.status).length,
    suspended: employees.filter(e => e.status === 'suspended').length,
    terminated: employees.filter(e => e.status === 'terminated').length,
    withBiometric: employees.filter(e => e.biometric_id).length
  };

  if (loading && !company) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6" data-testid="company-employees-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" onClick={() => navigate('/companies')} data-testid="back-button">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour aux entreprises
        </Button>
      </div>

      {/* Company Info */}
      {company && (
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              {company.logo_url ? (
                <img
                  src={getLogoUrl(company.logo_url)}
                  alt={company.name}
                  className="w-16 h-16 rounded-lg object-contain bg-white border"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Building2 className="w-8 h-8 text-primary" />
                </div>
              )}
              <div>
                <h1 className="text-2xl font-bold text-slate-900">{company.name}</h1>
                <p className="text-slate-600">{company.email}</p>
                <p className="text-sm text-slate-500">{company.address} - {company.country}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 mx-auto text-primary mb-2" />
            <p className="text-2xl font-bold">{stats.total}</p>
            <p className="text-xs text-slate-500">Total employés</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <CheckCircle2 className="w-6 h-6 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold text-green-600">{stats.active}</p>
            <p className="text-xs text-slate-500">Actifs</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <AlertCircle className="w-6 h-6 mx-auto text-amber-500 mb-2" />
            <p className="text-2xl font-bold text-amber-600">{stats.suspended}</p>
            <p className="text-xs text-slate-500">Suspendus</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <UserX className="w-6 h-6 mx-auto text-red-500 mb-2" />
            <p className="text-2xl font-bold text-red-600">{stats.terminated}</p>
            <p className="text-xs text-slate-500">Renvoyés</p>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 text-center">
            <User className="w-6 h-6 mx-auto text-blue-500 mb-2" />
            <p className="text-2xl font-bold text-blue-600">{stats.withBiometric}</p>
            <p className="text-xs text-slate-500">Biométrie OK</p>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="w-5 h-5 text-primary" />
            Filtres et recherche
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par nom, email, matricule..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="rounded-md border border-slate-300 p-2 min-w-[150px]"
            >
              <option value="">Tous les statuts</option>
              <option value="active">Actifs</option>
              <option value="suspended">Suspendus</option>
              <option value="terminated">Renvoyés</option>
            </select>
            <select
              value={departmentFilter}
              onChange={(e) => setDepartmentFilter(e.target.value)}
              className="rounded-md border border-slate-300 p-2 min-w-[150px]"
            >
              <option value="">Toutes les directions</option>
              {departments.map(dept => (
                <option key={dept.id} value={dept.id}>{dept.name}</option>
              ))}
            </select>
            <Button onClick={handleSearch}>
              <Search className="w-4 h-4 mr-2" />
              Rechercher
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Employees Table */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Liste des employés ({employees.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="rounded-md border border-slate-200 overflow-hidden overflow-x-auto">
            <Table>
              <TableHeader className="bg-slate-50">
                <TableRow>
                  <TableHead className="font-semibold">Matricule</TableHead>
                  <TableHead className="font-semibold">Nom</TableHead>
                  <TableHead className="font-semibold">Email</TableHead>
                  <TableHead className="font-semibold">Direction</TableHead>
                  <TableHead className="font-semibold">Poste</TableHead>
                  <TableHead className="font-semibold">Statut</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-8">
                      Aucun employé trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  employees.map((employee) => (
                    <TableRow key={employee.id} className="hover:bg-slate-50">
                      <TableCell className="font-mono font-medium">{employee.matricule}</TableCell>
                      <TableCell className="font-medium">
                        {employee.first_name} {employee.last_name}
                      </TableCell>
                      <TableCell className="text-sm">{employee.email}</TableCell>
                      <TableCell>
                        {employee.department_name ? (
                          <div className="flex items-center gap-1 text-sm">
                            <Building className="w-3 h-3 text-slate-400" />
                            {employee.department_name}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-sm">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{employee.position}</TableCell>
                      <TableCell>
                        {getStatusBadge(employee.status || 'active')}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => navigate(`/employees/${employee.id}`)}
                          title="Voir le profil détaillé"
                          data-testid={`view-employee-${employee.id}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          Enquêter
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
